<template>
  <div class="root">
    <TheHeader v-if="hasValidUser" />
    <div
      v-if="isErrorNotFound"
      class="root__content root__content--message"
    >
      <h1 class="root__title">
        404
      </h1>
      <h2>Sorry... we could not find the page you are looking for.</h2>
      <br>
      <router-link
        to="/"
        class="root__error-link"
      >
        Go back to home
      </router-link>
    </div>
    <div
      v-else-if="canAccessResource"
      class="root__content"
    >
      <transition
        name="bulk-edit-drawer"
        mode="out-in"
      >
        <StyleBulkEditDrawer
          v-if="shouldShowStyleBulkEditDrawer"
          key="style-bulk-edit-drawer"
          class="root__bulk-edit-drawer"
          :selected-style-ids="selectedStyleIds"
          :available-filters="availableFilters"
          :style-attributes="styleAttributes"
          :tags="styleTags"
          @close="closeStyleBulkSelectionMode"
          @apply="onApplyBulkStyleChanges"
        />

        <CollapsibleAside
          v-else-if="showMenu"
          key="main-menu"
          class="root__aside-left-container"
          :default-collapsed="true"
          panel-name="Menu"
          expanded-width="216px"
          collapsed-width="76px"
          side="left"
        >
          <TheAsideLeft class="root__aside-left" />
        </CollapsibleAside>
      </transition>
      <div
        :class="[
          'root__main',
          {
            'root__main--full-width': !showMenu,
            'root__main--bulk-edit': shouldShowStyleBulkEditDrawer
          }
        ]"
      >
        <router-view
          @bulk-delete="onBulkDeleteStyles"
          @bulk-update-status="onBulkUpdateStatus"
        />
      </div>
    </div>
    <div
      v-else-if="hasValidUser"
      class="root__content root__content--message"
    >
      <div v-if="isVendorUser">
        <template v-if="!user.vendor_company">
          <h2>
            Hang tight! There's nothing to see just yet while your account
            awaits admin approval.
          </h2>
          <br>
          Once it's approved, you'll get a confirmation email.
          <br>
          If you have any questions in the meantime, don't hesitate to reach out
          to
          <a href="mailto:3DSupport@pvh.com">3DSupport@pvh.com</a>
        </template>
        <template v-else-if="!hasPermissions">
          <h2>Hang tight! There's nothing to see just yet.</h2>
          <br>
          Please wait for the admin to assign a team(s) to your vendor company.
          <br>
          If you have any questions in the meantime, don't hesitate to reach out
          to
          <a href="mailto:3DSupport@pvh.com">3DSupport@pvh.com</a>
        </template>
      </div>
      <div v-else>
        <h2>Nothing to see yet 🙈</h2>
        <br>
        <h3>Ask for an invite from a colleague</h3>

        <h3>
          or
          <router-link
            to="/"
            class="root__error-link"
          >
            go to the homepage
          </router-link>
        </h3>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import VueTypes from 'vue-types'
import Sentry from '@/services/sentry'
import FeatureFlags from '@/services/featureFlags'
import Utils from '@/services/utils'
import { canUserAccessUserManagement } from '@/services/permissions'
import { PluginUtils } from '@/mixins/pluginUtils'
import { BrowzwearPlugin } from '@/mixins/browzwearPlugin'
import { USER_ROLE } from '@/constants/roleType'
import { LIBRARY_TYPE } from '@/constants/libraryType'
import TheAsideLeft from '@/components/layout/TheAsideLeft'
import CollapsibleAside from '@/components/layout/CollapsibleAside.vue'
import StyleBulkEditDrawer from '@/components/library/LibraryItems/components/StyleBulkEditDrawer.vue'

const ERROR_CODE_NOT_FOUND = 404

export default {
  name: 'Root',

  components: {
    TheAsideLeft,
    CollapsibleAside,
    StyleBulkEditDrawer
  },

  mixins: [PluginUtils, BrowzwearPlugin],

  props: {
    showMenu: VueTypes.bool.def(true)
  },

  data () {
    return {
      errorCode: null,
      isBulkUpdatingStyles: false
    }
  },

  computed: {
    ...mapGetters({
      user: 'getCognitoUserData',
      isNotFound: 'getIsNotFound',
      activeWorkspace: 'getActiveWorkspace',
      activeLibraryType: 'getActiveLibraryType',
      isStyleBulkSelectionMode: 'getStyleBulkSelectionMode',
      selectedStyleIds: 'getSelectedStyleIds'
    }),

    ...mapGetters(['getAvailableFilters', 'getStylesAttributes', 'getTags']),

    /**
     * @returns {boolean}
     */
    canAccessResource () {
      return this.hasPermissions
    },

    /**
     * @returns {boolean}
     */
    hasPermissions () {
      return Boolean(this.user && this.user.teams && this.user.teams.length > 0)
    },

    /**
     * @returns {boolean}
     */
    hasValidUser () {
      return Boolean(this.user && this.user.email)
    },

    /**
     * @returns {boolean}
     */
    isVendorUser () {
      return this.user && this.user.role === USER_ROLE.VENDOR.name
    },

    /**
     * @returns {boolean}
     */
    isErrorNotFound () {
      return this.errorCode === ERROR_CODE_NOT_FOUND || this.isNotFound
    },

    /**
     * @returns {boolean}
     */
    isRootPath () {
      return this.$route.path === '/'
    },

    /**
     * @returns {boolean}
     */
    isAdminToolsPath () {
      return this.$route.path === '/admin-tools'
    },

    /**
     * Only replace the left navigation while bulk-selecting styles.
     *
     * @returns {boolean}
     */
    shouldShowStyleBulkEditDrawer () {
      return Boolean(
        this.showMenu &&
          this.isStyleBulkSelectionMode &&
          this.activeLibraryType === LIBRARY_TYPE.STYLE
      )
    },

    /**
     * @returns {object}
     */
    availableFilters () {
      return this.getAvailableFilters() || {}
    },

    /**
     * @returns {object[]}
     */
    styleAttributes () {
      return this.getStylesAttributes || []
    },

    /**
     * @returns {object[]}
     */
    styleTags () {
      return (
        this.getTags({
          libraryType: LIBRARY_TYPE.STYLE
        }) || []
      )
    }
  },

  watch: {
    /**
     */
    $route () {
      this.redirectToDefaultLibraryTypeIfRootPath()
      this.setErrorCode()
      this.setIsNotFound(false)

      if (this.activeLibraryType !== LIBRARY_TYPE.STYLE) {
        this.closeStyleBulkSelectionMode()
      }
    }
  },

  async created () {
    await this.$featureFlagsManager.initialize()
    await this.fetchCognitoUserData()

    this.redirectToDefaultLibraryTypeIfRootPath()
    this.setErrorCode()

    if (this.user.email) {
      Sentry.identifyUserByEmail(this.user.email)

      this.$tracking.identifyUser(this.user)
      Utils.trackLogin(this.user, this.platform)
    }
  },

  methods: {
    ...mapActions([
      'fetchCognitoUserData',
      'setIsNotFound',
      'setStyleBulkSelectionMode',
      'clearSelectedStyleIds',
      'bulkUpdateStyles',
      'bulkDeleteStyles'
    ]),

    /**
     * Close the bulk-edit drawer and exit selection mode.
     */
    closeStyleBulkSelectionMode () {
      if (!this.isStyleBulkSelectionMode) {
        return
      }

      this.setStyleBulkSelectionMode(false)
      this.clearSelectedStyleIds()
    },

    /**
     * @param   {object}        payload
     * @param   {number[]}      payload.styleIds
     * @param   {object}        payload.changes
     *
     * @returns {Promise<void>}
     */
    async onApplyBulkStyleChanges (payload) {
      if (
        this.isBulkUpdatingStyles ||
        !payload.styleIds.length ||
        !Object.keys(payload.changes).length
      ) {
        return
      }

      try {
        await this.$confirm(
          `Apply the selected changes to ${payload.styleIds.length} styles?`,
          'Confirm bulk update',
          {
            confirmButtonText: 'Apply',
            cancelButtonText: 'Cancel',
            type: 'warning',
            showClose: false
          }
        )
      } catch (error) {
        return
      }

      this.isBulkUpdatingStyles = true

      try {
        await this.bulkUpdateStyles(payload)

        this.$message({
          message: `${payload.styleIds.length} styles have been updated successfully.`,
          type: 'success',
          showClose: true
        })

        this.closeStyleBulkSelectionMode()
      } catch (error) {
        const responseData = error && error.response && error.response.data

        const errorMessage =
          responseData &&
          (responseData.detail || responseData.message || responseData.error)

        this.$message({
          message:
            errorMessage ||
            'The styles could not be updated. Please try again.',
          type: 'error',
          showClose: true
        })
      } finally {
        this.isBulkUpdatingStyles = false
      }
    },

    /**
     *
     * @param {object}   root0
     * @param {string}   root0.status
     * @param {number[]} root0.styleIds
     */
    onBulkUpdateStatus ({ status, styleIds }) {
      if (!styleIds || !styleIds.length || !status) {
        return
      }

      this.onApplyBulkStyleChanges({
        styleIds,
        changes: { asset_status: status }
      })
    },

    /**
     * @param {number[]} styleIds
     */
    async onBulkDeleteStyles (styleIds) {
      if (!styleIds || !styleIds.length) {
        return
      }

      try {
        await this.$confirm(
          `Delete ${styleIds.length} selected styles?`,
          'Confirm bulk delete',
          {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            type: 'warning',
            showClose: false
          }
        )
      } catch (error) {
        return
      }

      try {
        await this.bulkDeleteStyles({ styleIds })

        this.$message({
          message: `${styleIds.length} styles have been deleted successfully.`,
          type: 'success',
          showClose: true
        })

        this.closeStyleBulkSelectionMode()
        this.libraryRefreshKey += 1
      } catch (error) {
        const responseData = error && error.response && error.response.data

        const errorMessage =
          responseData &&
          (responseData.detail || responseData.message || responseData.error)

        this.$message({
          message:
            errorMessage ||
            'The styles could not be deleted. Please try again.',
          type: 'error',
          showClose: true
        })
      }
    },

    /**
     * Set the current route error code.
     */
    setErrorCode () {
      const check = this.$route.meta.check

      if (check && !check()) {
        this.errorCode = ERROR_CODE_NOT_FOUND
      } else {
        this.errorCode = null
      }
    },

    /**
     * Redirect root and unauthorized admin routes.
     */
    redirectToDefaultLibraryTypeIfRootPath () {
      if (FeatureFlags.canUserAccessWorkspaces() && !this.activeWorkspace) {
        if (this.hasValidUser) {
          this.$router.push({
            name: 'TheWorkspaces'
          })
        }

        return
      }

      if (
        this.isRootPath ||
        (this.isAdminToolsPath && !canUserAccessUserManagement())
      ) {
        this.$router.push({
          name: 'LibraryStylesTypes'
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$root-height: calc(100vh - #{$main-header-height});
$bulk-edit-drawer-width: spacing(34);

.root {
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.root__content {
  display: flex;
  align-items: center;
  width: 100%;
  height: $root-height;
  min-height: 0;
  overflow: hidden;
}

.root__content--message {
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.root__title {
  @include text-title-big;
}

.root__aside-left-container {
  flex: 0 0 auto;
  align-self: stretch;
  height: 100%;
}

.root__aside-left {
  width: 100%;
  height: 100%;
}

.root__bulk-edit-drawer {
  flex: 0 0 $bulk-edit-drawer-width;
  align-self: stretch;
  width: $bulk-edit-drawer-width;
  min-width: $bulk-edit-drawer-width;
  height: 100%;
  overflow: hidden;
  background: $white;
  border-right: $border-divider;
  box-shadow: $shadow-default;
}

.root__main {
  flex: 1 1 auto;
  min-width: 0;
  height: $root-height;
  overflow: hidden;

  @media (max-width: $media-query-s) {
    padding-right: spacing(4);
  }
}

.root__main--full-width {
  width: 100%;
}

.root__main--bulk-edit {
  padding-left: spacing(3);
}

.root__error-link {
  color: $blue;
}

.bulk-edit-drawer-enter-active,
.bulk-edit-drawer-leave-active {
  transition:
    width 0.25s ease,
    min-width 0.25s ease,
    flex-basis 0.25s ease,
    opacity 0.2s ease,
    transform 0.25s ease;
}

.bulk-edit-drawer-enter,
.bulk-edit-drawer-leave-to {
  flex-basis: 0;
  width: 0;
  min-width: 0;
  transform: translateX(-100%);
  opacity: 0;
}
</style>
